/**
 * RealTimeGexChart — Lightweight Charts OHLC + GEX price lines near spot.
 */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { lwChartColours, readChartTheme, useChartTheme } from "../theme/chartTheme";
import {
    CandlestickData,
    CandlestickSeries,
    createChart,
    IChartApi,
    IPriceLine,
    ISeriesApi,
    Time,
} from "lightweight-charts";
import { getApiUrl } from "../config/api";
import {
    chartStockWebSocketService,
} from "../services/polygonWebSocketService";
import type { ChartStockLiveTick } from "../services/chartStockLiveTypes";
import {
    calculateGexByDte,
    discoverDtes,
    GexGroup,
    GexLevels,
    GexLevelSet,
    OptionRow,
    StrikeAgg,
} from "../services/gexCalculator";
import { fetchGexChainForSymbol } from "../services/gexChainDataService";
import {
    computeIndexEtfScale,
    scaleEtfToIndex,
} from "../services/indexChartLiveScale";
import { polygonIndexWebSocketService } from "../services/polygonIndexWebSocketService";
import {
    getChartSymbol,
    getPolygonIndexSymbol,
    getPriceSourceLabel,
    indexChartScale,
    isIndexEtfProxy,
    isIndexGexSymbol,
    resolveSymbol,
    usesNativeIndexChart,
} from "../services/symbolPricingService";
import { getUnderlyingPriceFallback } from "../utils/gammaCalculations";
import {
    buildFlowOpportunityMarkers,
    buildFlowVolumeProfile,
    filterFlowTradesForSymbol,
} from "../utils/flowVolumeProfile";
import FlowVolumeProfileOverlay from "./charts/FlowVolumeProfileOverlay";
import RtlLegacyVolumeProfileOverlay from "./charts/RtlLegacyVolumeProfileOverlay";
import { FlowOpportunityMarkersPrimitive } from "./charts/FlowOpportunityMarkersPrimitive";
import GexAnalyticsStrip from "./analytics/GexAnalyticsStrip";
import { RegimeSnapshotProvider } from "./analytics/RegimeSnapshotBridge";
import { buildMarketAnalyticsSnapshot } from "../utils/marketRegimeEngine";
import { useFlowData } from "./charts/gex/hooks/useFlowData";
import { useRtlLegacyScannerData } from "../hooks/useRtlLegacyScannerData";
import {
    buildRtlLegacyStrikeProfile,
    type RtlLegacyProfileMetric,
} from "../utils/rtlLegacyStrikeProfile";
import { useTheme } from "../contexts/ThemeContext";
import { useMmecFx } from "../theme/mmecFx";
import { isDesignSystemOn } from "../theme/mmecDsPreview";

const CANDLE_UP = "#e8eaed";
const CANDLE_DOWN = "#1a1a1a";
const CANDLE_WICK_UP = "#e8eaed";
const CANDLE_WICK_DOWN = "#6b7280";

const NEAR_SPOT_PCT = 0.1;
const BAR_MS = 5 * 60 * 1000;
const MAX_BARS = 300;
/** Poll latest 1m bar when WS is quiet (delayed Polygon A. = ~1 bar/min). */
const LIVE_POLL_MS = 5_000;
/** Prominent live price line (drawn last so it sits above GEX / flow lines). */
const SPOT_LINE_COLOR = "rgba(240, 243, 246, 0.75)";
/* Design System preview (?ds=1) only; hexes from theme/mmec-ds/tokens.css (lightweight-charts cannot
   read CSS variables). Levels take the four-hue scheme: walls magenta (CW solid, PW dashed), gamma
   levels blue (GF / ZG solid); the volume-built set is the intraday read, so it goes dotted. The DTE
   stays in each line's title. */
const DS_SPOT_LINE_COLOR = "#b2b5be";
const DS_LEVEL_COLOR: Record<string, string> = { gammaFlip: "#2962ff", zeroGamma: "#2962ff", callWall: "#e040fb", putWall: "#e040fb" };
const spotLineColor = (): string => (isDesignSystemOn() ? DS_SPOT_LINE_COLOR : SPOT_LINE_COLOR);
/** lightweight-charts LineStyle: 0 solid, 1 dotted, 2 dashed */
const SPOT_LINE_STYLE = 1;
const SPOT_LINE_WIDTH = 1;

const DTE_COLORS: Record<number, string> = {
    0: "#FF3B30",
    1: "#FF9500",
    2: "#FFCC00",
    3: "#34C759",
    7: "#0A84FF",
    14: "#AF52DE",
    30: "#8E8E93",
};
const ALL_DTE_COLOR = "#5AC8FA";

const GEX_LINE_SPECS = [
    { key: "gammaFlip" as const, short: "GF" },
    { key: "zeroGamma" as const, short: "ZG" },
    { key: "callWall" as const, short: "CW" },
    { key: "putWall" as const, short: "PW" },
];

/**
 * Which exposure the levels are built from. Open interest is the book carried into today; volume is
 * what has traded since the open. They separate when positioning is actively moving, which is the
 * whole reason this chart exists as something other than a copy of the GEX Chart.
 */
export type GexBasis = "oi" | "volume" | "both";

/** Volume lines are dotted so they read as today's activity against the solid standing book. */
const VOL_LINE_STYLE = 1;
const OI_LINE_STYLE = 2;

export interface RealTimeGexChartProps {
    symbol: string;
    fetchOptionChain?: (symbol: string) => Promise<OptionRow[]>;
    refreshInterval?: number;
    /** MMEC simple shell: fill column, compact chrome, flow VP on by default */
    simpleLayout?: boolean;
    /** Called when user changes symbol in the chart header (simple shell). */
    onSymbolChange?: (symbol: string) => void;
}

function colorForDte(dte: number | string): string {
    // Design System: no per-DTE hues (rule 5) — DTE lives in the label; chips and group borders are neutral.
    if (isDesignSystemOn()) return "#b2b5be";
    if (dte === "all") return ALL_DTE_COLOR;
    const keys = [0, 1, 2, 3, 7, 14, 30];
    const n = Number(dte);
    const k = keys.reduce(
        (p, c) => (Math.abs(c - n) < Math.abs(p - n) ? c : p),
        0,
    );
    return DTE_COLORS[k] || ALL_DTE_COLOR;
}

function bucketTs(ts: number): number {
    return Math.floor(ts / BAR_MS) * BAR_MS;
}

/**
 * Re-derive one exposure series' levels from the strikes near spot only. Far-dated size dominates a
 * whole-chain reduction and drags the walls out of the traded range; this chart is about the levels
 * price can actually reach today.
 */
function refineSeriesNearSpot(
    near: StrikeAgg[],
    anchor: number,
    spotGammaOf: (s: StrikeAgg) => number,
    gexOf: (s: StrikeAgg) => number,
    callSizeOf: (s: StrikeAgg) => number,
    putSizeOf: (s: StrikeAgg) => number,
) {
    const peakSG = near.reduce(
        (m, s) => (Math.abs(spotGammaOf(s)) > Math.abs(spotGammaOf(m)) ? s : m),
        near[0],
    );
    const callsAbove = near.filter((s) => s.strike >= anchor * 0.998);
    const putsBelow = near.filter((s) => s.strike <= anchor * 1.002);
    const callPool = callsAbove.length ? callsAbove : near;
    const putPool = putsBelow.length ? putsBelow : near;
    const callWall = callPool.reduce(
        (m, s) => (callSizeOf(s) > callSizeOf(m) ? s : m),
        callPool[0],
    );
    const putWall = putPool.reduce(
        (m, s) => (putSizeOf(s) > putSizeOf(m) ? s : m),
        putPool[0],
    );

    const sorted = [...near].sort((a, b) => a.strike - b.strike);
    let cum = 0;
    let zeroGamma: number | null = null;
    let netGex = 0;
    for (const s of sorted) {
        const prev = cum;
        cum += gexOf(s);
        netGex = cum;
        if (
            zeroGamma === null &&
            ((prev < 0 && cum >= 0) || (prev > 0 && cum <= 0))
        ) {
            zeroGamma = s.strike;
        }
    }

    return {
        gammaFlip: peakSG.strike,
        zeroGamma,
        callWall: callWall.strike,
        putWall: putWall.strike,
        callWallSize: callSizeOf(callWall),
        putWallSize: putSizeOf(putWall),
        netGex,
    };
}

function refineGexLevelsNearSpot(
    levels: GexLevels,
    anchor: number,
    pct = NEAR_SPOT_PCT,
): GexLevels {
    if (!anchor || anchor <= 0 || !levels.strikes?.length) return levels;
    const band = anchor * pct;
    const near = levels.strikes.filter(
        (s) => Math.abs(s.strike - anchor) <= band,
    );
    if (near.length < 2) return levels;

    const oi = refineSeriesNearSpot(
        near,
        anchor,
        (s) => s.netSpotGamma,
        (s) => s.netGex,
        (s) => s.callOI,
        (s) => s.putOI,
    );
    const vol = refineSeriesNearSpot(
        near,
        anchor,
        (s) => s.netVolSpotGamma,
        (s) => s.netVolGex,
        (s) => s.callVol,
        (s) => s.putVol,
    );

    return {
        ...levels,
        gammaFlip: oi.gammaFlip,
        zeroGamma: oi.zeroGamma,
        callWall: oi.callWall,
        putWall: oi.putWall,
        callWallOI: oi.callWallSize,
        putWallOI: oi.putWallSize,
        netGex: oi.netGex,
        volume: vol,
    };
}

function levelNearSpot(
    levels: GexLevels | GexLevelSet | undefined,
    key: (typeof GEX_LINE_SPECS)[number]["key"],
    anchor: number,
): number | null {
    if (!levels || !anchor || anchor <= 0) return null;
    const v = levels[key];
    if (v == null || !Number.isFinite(Number(v)) || Number(v) <= 0) return null;
    const n = Number(v);
    if (Math.abs(n - anchor) / anchor > NEAR_SPOT_PCT) return null;
    return n;
}

const INDEX_QUICK_PICKS = ["SPX", "SPXW", "SP500", "NDX", "RUT", "VIX"] as const;

function readRtlDataMode(): "accumulated" | "5min" {
    try {
        const m = localStorage.getItem("mmec_rtl_data_mode");
        return m === "5min" ? "5min" : "accumulated";
    } catch {
        return "accumulated";
    }
}

export type ChartLiveFeedMode =
    | "index_ws"
    | "scaled_ws"
    | "stock_ws"
    | "delayed_poll";

function liveFeedBadge(mode: ChartLiveFeedMode): string {
    switch (mode) {
        case "index_ws":
            return "LIVE · index WS";
        case "scaled_ws":
            return "LIVE · index (ETF tick)";
        case "stock_ws":
            return "LIVE · WS";
        default:
            return "DELAYED · poll";
    }
}

const RealTimeGexChart: React.FC<RealTimeGexChartProps> = ({
    symbol,
    fetchOptionChain,
    refreshInterval = 40000,
    simpleLayout = false,
    onSymbolChange,
}) => {
    const { colors } = useTheme();
    const fx = useMmecFx();

    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const chartTheme = useChartTheme();
    useEffect(() => {
        chartRef.current?.applyOptions(lwChartColours(chartTheme));
    }, [chartTheme]);
    const candleRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
    const candleBuf = useRef<Map<number, CandlestickData>>(new Map());
    const gexLines = useRef<IPriceLine[]>([]);
    const flowLines = useRef<IPriceLine[]>([]);
    const spotLineRef = useRef<IPriceLine | null>(null);
    const flowMarkersPrimitiveRef = useRef<FlowOpportunityMarkersPrimitive | null>(
        null,
    );
    const prevChartScaleRef = useRef(1);
    const initialFitDoneRef = useRef(false);
    const gexAnchorRef = useRef(0);
    const displaySpotRef = useRef(0);
    const indexEtfScaleRef = useRef(1);

    const [inputSymbol, setInputSymbol] = useState(symbol.toUpperCase());
    const [displaySpot, setDisplaySpot] = useState(0);
    displaySpotRef.current = displaySpot;
    const [etfLivePrice, setEtfLivePrice] = useState(0);
    const [chainSpot, setChainSpot] = useState(0);
    const [chainRows, setChainRows] = useState<OptionRow[]>([]);
    const [gexBasis, setGexBasis] = useState<GexBasis>("both");
    /** Blank means unbounded; kept as text so the field can be cleared while typing. */
    const [minVol, setMinVol] = useState("");
    const [maxVol, setMaxVol] = useState("");
    const [selectedDtes, setSelectedDtes] = useState<Set<number | string>>(
        () => new Set(["all"]),
    );
    const [availableDtes, setAvailableDtes] = useState<number[]>([]);
    const [lastUpdate, setLastUpdate] = useState("");
    const [rowCount, setRowCount] = useState(0);
    const [loadingGex, setLoadingGex] = useState(false);
    const [wsStatus, setWsStatus] = useState<
        "disconnected" | "connecting" | "connected" | "streaming"
    >("disconnected");
    const lastTickAtRef = useRef(0);
    const [fetchError, setFetchError] = useState("");
    const [chainUnderlying, setChainUnderlying] = useState("");
    const [chartReady, setChartReady] = useState(false);
    const [hasCandles, setHasCandles] = useState(false);
    /** Why there are no bars, or why the ones shown are old. Empty when the feed is healthy. */
    const [barsNotice, setBarsNotice] = useState("");
    const [showFlowVp, setShowFlowVp] = useState(true);
    const [showRtlVp, setShowRtlVp] = useState(true);
    const [rtlMetric, setRtlMetric] = useState<RtlLegacyProfileMetric>("delta");
    const [rtlDataMode, setRtlDataMode] = useState<"accumulated" | "5min">(
        readRtlDataMode,
    );
    const [liveFeedMode, setLiveFeedMode] =
        useState<ChartLiveFeedMode>("stock_ws");

    const { rows: rtlScannerRows, loading: rtlLoading, error: rtlError } =
        useRtlLegacyScannerData(30_000);

    useEffect(() => {
        const onMode = (e: Event) => {
            const mode = (e as CustomEvent<{ mode?: string }>).detail?.mode;
            if (mode === "5min" || mode === "accumulated") {
                setRtlDataMode(mode);
            }
        };
        window.addEventListener("mmecDataModeChanged", onMode);
        return () => window.removeEventListener("mmecDataModeChanged", onMode);
    }, []);

    useEffect(() => {
        if (symbol) {
            setInputSymbol(symbol.toUpperCase());
            setChainUnderlying("");
        }
    }, [symbol]);

    useEffect(() => {
        onSymbolChange?.(inputSymbol);
    }, [inputSymbol, onSymbolChange]);

    const resolved = resolveSymbol(inputSymbol);
    const displaySymbol = resolved?.displaySymbol || inputSymbol.toUpperCase();
    const chartSymbol = getChartSymbol(inputSymbol);
    const nativeIndexChart = usesNativeIndexChart(resolved);
    const wsSymbol = resolved?.wsSymbol || inputSymbol.toUpperCase();
    const gexUnderlying =
        chainUnderlying || resolved?.gexUnderlying || inputSymbol.toUpperCase();
    const priceLabel = getPriceSourceLabel(inputSymbol);
    const indexProxy = isIndexEtfProxy(resolved);
    const indexGex = isIndexGexSymbol(inputSymbol);

    const chartScale = useMemo(
        () =>
            nativeIndexChart
                ? 1
                : indexChartScale(resolved, chainSpot, etfLivePrice || displaySpot),
        [nativeIndexChart, resolved, chainSpot, etfLivePrice, displaySpot],
    );

    const polygonIndexSymbol = getPolygonIndexSymbol(inputSymbol);

    const indexLiveScale = useMemo(() => {
        if (!nativeIndexChart) return 1;
        return computeIndexEtfScale(
            chainSpot || displaySpot,
            etfLivePrice,
            indexEtfScaleRef.current,
        );
    }, [nativeIndexChart, chainSpot, displaySpot, etfLivePrice]);

    useEffect(() => {
        if (nativeIndexChart && chainSpot > 0 && etfLivePrice > 0) {
            indexEtfScaleRef.current = computeIndexEtfScale(
                chainSpot,
                etfLivePrice,
                indexEtfScaleRef.current,
            );
        }
    }, [nativeIndexChart, chainSpot, etfLivePrice]);

    /** GEX level band anchor (chain spot) — updates on GEX refresh, not every WS tick. */
    const gexAnchor = chainSpot > 0 ? chainSpot : gexAnchorRef.current;

    const flowSymbol = gexUnderlying;
    const flowFetchOpts = useMemo(
        () => ({
            symbolScoped: true,
            historyHours: 24,
            limit: 12_000,
            minPremium: 1000,
        }),
        [],
    );
    const { flowTrades, optionsProfile, loading: flowLoading, flowMeta } =
        useFlowData(flowSymbol, "full", flowFetchOpts);

    const scopedFlowTrades = useMemo(
        () =>
            filterFlowTradesForSymbol(
                flowTrades,
                gexUnderlying,
                inputSymbol,
                resolved?.displaySymbol || "",
            ),
        [flowTrades, gexUnderlying, inputSymbol, resolved?.displaySymbol],
    );

    const flowProfileLevels = useMemo(() => {
        if (!showFlowVp || gexAnchor <= 0) return [];
        return buildFlowVolumeProfile(
            optionsProfile,
            scopedFlowTrades,
            gexAnchor,
            NEAR_SPOT_PCT + 0.02,
        );
    }, [showFlowVp, optionsProfile, scopedFlowTrades, gexAnchor]);

    const flowOpportunities = useMemo(() => {
        if (!showFlowVp || gexAnchor <= 0) return [];
        return buildFlowOpportunityMarkers(scopedFlowTrades, {
            anchor: gexAnchor,
            minPremium: 1_000,
            maxMarkers: 800,
            chartScale,
        });
    }, [showFlowVp, scopedFlowTrades, gexAnchor, chartScale]);

    const rtlProfileLevels = useMemo(() => {
        if (!showRtlVp || gexAnchor <= 0 || !rtlScannerRows.length) return [];
        return buildRtlLegacyStrikeProfile(
            rtlScannerRows,
            inputSymbol,
            rtlMetric,
            rtlDataMode,
            gexAnchor,
            NEAR_SPOT_PCT + 0.02,
            chartScale,
        );
    }, [
        showRtlVp,
        rtlScannerRows,
        inputSymbol,
        rtlMetric,
        rtlDataMode,
        gexAnchor,
        chartScale,
    ]);

    const toggleDte = useCallback((v: number | string) => {
        setSelectedDtes((prev) => {
            const next = new Set(prev);
            if (next.has(v)) next.delete(v);
            else next.add(v);
            if (next.size === 0) next.add("all");
            return next;
        });
    }, []);

    const pushCandlesToChart = useCallback((fitView = false) => {
        const series = candleRef.current;
        if (!series) return;
        const candles = Array.from(candleBuf.current.values()).sort(
            (a, b) => (a.time as number) - (b.time as number),
        );
        if (!candles.length) return;
        series.setData(candles);
        if (fitView) {
            chartRef.current?.timeScale().fitContent();
        }
    }, []);

    const scaleChartPrice = useCallback(
        (p: number, opts?: { indexNative?: boolean }) => {
            if (opts?.indexNative) return p;
            if (nativeIndexChart) {
                return scaleEtfToIndex(p, indexLiveScale);
            }
            return p * chartScale;
        },
        [nativeIndexChart, indexLiveScale, chartScale],
    );

    const mergeLiveTick = useCallback(
        (rawPrice: number, tsMs: number, opts?: { indexNative?: boolean }) => {
            if (!opts?.indexNative) {
                setEtfLivePrice(rawPrice);
            }
            const price = scaleChartPrice(rawPrice, opts);
            setDisplaySpot(price);
            const bkt = bucketTs(tsMs);
            const t = Math.floor(bkt / 1000) as Time;
            const cur = candleBuf.current.get(bkt);
            const next: CandlestickData = cur
                ? {
                      ...cur,
                      high: Math.max(cur.high, price),
                      low: Math.min(cur.low, price),
                      close: price,
                  }
                : {
                      time: t,
                      open: price,
                      high: price,
                      low: price,
                      close: price,
                  };
            candleBuf.current.set(bkt, next);
            if (candleBuf.current.size > MAX_BARS) {
                const keys = Array.from(candleBuf.current.keys()).sort(
                    (a, b) => a - b,
                );
                while (candleBuf.current.size > MAX_BARS) {
                    candleBuf.current.delete(keys.shift()!);
                }
            }
            candleRef.current?.update(next);
            setHasCandles(true);
        },
        [scaleChartPrice],
    );

    const refreshGex = useCallback(async () => {
        setLoadingGex(true);
        try {
            setFetchError("");
            const { rows, usedUnderlying } = fetchOptionChain
                ? {
                      rows: await fetchOptionChain(gexUnderlying),
                      usedUnderlying: gexUnderlying,
                  }
                : await fetchGexChainForSymbol(inputSymbol);

            setChainUnderlying(usedUnderlying);
            setRowCount(rows.length);
            if (!rows.length) {
                                setChainRows([]);
                const hint = indexGex
                    ? " — fetch SPXW in Stable Data first, then retry"
                    : "";
                setFetchError(`No chain data for ${displaySymbol}${hint}`);
                return;
            }

            setAvailableDtes(discoverDtes(rows));
            setChainRows(rows);
            setLastUpdate(new Date().toLocaleTimeString());

            const spot = getUnderlyingPriceFallback(rows, "accumulated");
            if (spot > 0) {
                setChainSpot(spot);
                gexAnchorRef.current = spot;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            setFetchError(msg);
                        setChainRows([]);
        } finally {
            setLoadingGex(false);
        }
    }, [
        inputSymbol,
        displaySymbol,
        gexUnderlying,
        fetchOptionChain,
        selectedDtes,
        indexProxy,
        indexGex,
    ]);

    const syncIndexEtfScaleFromBars = useCallback(
        async (from: string, to: string) => {
            if (!nativeIndexChart || !wsSymbol) return;
            try {
                const etfUrl = getApiUrl(
                    `/api/historical/equity-bars?symbol=${encodeURIComponent(wsSymbol)}&interval=5m&from=${from}&to=${to}`,
                );
                const etfRes = await fetch(etfUrl);
                if (!etfRes.ok) return;
                const etfJson = await etfRes.json();
                const etfBars = (etfJson.bars || []) as { c?: number; o?: number }[];
                const idxBars = Array.from(candleBuf.current.values());
                if (!etfBars.length || !idxBars.length) return;
                const lastEtf =
                    Number(etfBars[etfBars.length - 1]?.c) ||
                    Number(etfBars[etfBars.length - 1]?.o) ||
                    0;
                const lastIdx = idxBars[idxBars.length - 1]?.close ?? 0;
                if (lastEtf > 0 && lastIdx > 0) {
                    indexEtfScaleRef.current = computeIndexEtfScale(
                        lastIdx,
                        lastEtf,
                        indexEtfScaleRef.current,
                    );
                }
            } catch {
                /* optional */
            }
        },
        [nativeIndexChart, wsSymbol],
    );

    const applyBarFromApi = useCallback(
        (b: {
            t: number | string;
            o?: number;
            h?: number;
            l?: number;
            c?: number;
        }) => {
            const tsMs =
                typeof b.t === "number" ? b.t : new Date(b.t).getTime();
            const barClose = Number(b.c) || Number(b.o) || 0;
            if (barClose <= 0) return;
            const indexNative = nativeIndexChart;
            if (!indexNative) {
                setEtfLivePrice(barClose);
            }
            const open = scaleChartPrice(Number(b.o) || barClose, {
                indexNative,
            });
            const close = scaleChartPrice(barClose, { indexNative });
            const high = scaleChartPrice(
                Math.max(
                    Number(b.h) || barClose,
                    Number(b.o) || barClose,
                    barClose,
                ),
                { indexNative },
            );
            const low = scaleChartPrice(
                Math.min(
                    Number(b.l) || barClose,
                    Number(b.o) || barClose,
                    barClose,
                ),
                { indexNative },
            );
            const bkt = bucketTs(tsMs);
            const candle: CandlestickData = {
                time: Math.floor(bkt / 1000) as Time,
                open,
                high,
                low,
                close,
            };
            candleBuf.current.set(bkt, candle);
            if (candleRef.current) {
                candleRef.current.update(candle);
            }
            setDisplaySpot(close);
            lastTickAtRef.current = Date.now();
            setWsStatus("streaming");
            setHasCandles(true);
            if (indexNative) {
                setLiveFeedMode("delayed_poll");
            }
        },
        [scaleChartPrice, nativeIndexChart],
    );

    const loadHistoricalPrice = useCallback(async () => {
        try {
            const now = Date.now();
            const from = new Date(now - 5 * 86400000)
                .toISOString()
                .slice(0, 10);
            const to = new Date(now).toISOString().slice(0, 10);
            const url = getApiUrl(
                `/api/historical/equity-bars?symbol=${encodeURIComponent(chartSymbol)}&interval=5m&from=${from}&to=${to}`,
            );
            const res = await fetch(url);
            if (!res.ok) {
                // Returning silently here is what left the chart on "Loading OHLC bars…" forever.
                setBarsNotice(
                    `Bars request failed (HTTP ${res.status}). Will retry on the next refresh.`,
                );
                return;
            }
            const json = await res.json();
            const bars = (json.bars || []) as {
                t: number | string;
                o?: number;
                h?: number;
                l?: number;
                c?: number;
            }[];

            if (!bars.length) {
                setBarsNotice(
                    json.message ||
                        `No OHLC bars returned for ${chartSymbol} over ${from} → ${to}.`,
                );
            } else if (json.stale) {
                // The server already words this, age included, so both charts say the same thing.
                setBarsNotice(json.message || "Showing the last good bars.");
            } else {
                setBarsNotice("");
            }

            candleBuf.current.clear();
            for (const b of bars) {
                const tsMs =
                    typeof b.t === "number" ? b.t : new Date(b.t).getTime();
                const bkt = bucketTs(tsMs);
                const etfOpen = Number(b.o) || 0;
                const etfClose = Number(b.c) || etfOpen;
                if (etfClose <= 0) continue;
                const etfHigh = Math.max(
                    Number(b.h) || etfClose,
                    etfOpen,
                    etfClose,
                );
                const etfLow = Math.min(
                    Number(b.l) || etfClose,
                    etfOpen,
                    etfClose,
                );
                const indexNative = nativeIndexChart;
                candleBuf.current.set(bkt, {
                    time: Math.floor(bkt / 1000) as Time,
                    open: scaleChartPrice(etfOpen, { indexNative }),
                    high: scaleChartPrice(etfHigh, { indexNative }),
                    low: scaleChartPrice(etfLow, { indexNative }),
                    close: scaleChartPrice(etfClose, { indexNative }),
                });
            }

            const shouldFit = !initialFitDoneRef.current;
            pushCandlesToChart(shouldFit);
            if (shouldFit) initialFitDoneRef.current = true;
            const lastBar = bars[bars.length - 1];
            const lastClose =
                Number(lastBar?.c) || Number(lastBar?.o) || 0;
            if (lastClose > 0) {
                if (!nativeIndexChart) {
                    setEtfLivePrice(lastClose);
                }
                const scaledLast = scaleChartPrice(lastClose, {
                    indexNative: nativeIndexChart,
                });
                setDisplaySpot(scaledLast);
                if (gexAnchorRef.current <= 0) {
                    gexAnchorRef.current = scaledLast;
                }
                lastTickAtRef.current = Date.now();
            }
            setHasCandles(candleBuf.current.size > 0);
            if (nativeIndexChart) {
                await syncIndexEtfScaleFromBars(from, to);
            }
        } catch (err: unknown) {
            // Swallowing this left no candles and no explanation — the spinner simply never ended.
            setBarsNotice(
                `Could not load OHLC bars: ${err instanceof Error ? err.message : String(err)}`,
            );
        }
    }, [
        chartSymbol,
        nativeIndexChart,
        pushCandlesToChart,
        scaleChartPrice,
        syncIndexEtfScaleFromBars,
    ]);

    useEffect(() => {
        const prev = prevChartScaleRef.current;
        if (prev === chartScale || !candleBuf.current.size) {
            prevChartScaleRef.current = chartScale;
            return;
        }
        const factor = chartScale / prev;
        if (factor !== 1) {
            for (const [k, c] of Array.from(candleBuf.current.entries())) {
                candleBuf.current.set(k, {
                    ...c,
                    open: c.open * factor,
                    high: c.high * factor,
                    low: c.low * factor,
                    close: c.close * factor,
                });
            }
            pushCandlesToChart(false);
            if (displaySpot > 0) {
                setDisplaySpot((p) => p * factor);
            }
        }
        prevChartScaleRef.current = chartScale;
    }, [chartScale, pushCandlesToChart, displaySpot]);

    const pollLatestBar = useCallback(async () => {
        try {
            const today = new Date().toISOString().slice(0, 10);
            const url = getApiUrl(
                `/api/historical/equity-bars?symbol=${encodeURIComponent(chartSymbol)}&interval=1m&from=${today}&to=${today}`,
            );
            const res = await fetch(url);
            if (!res.ok) return;
            const json = await res.json();
            const bars = (json.bars || []) as {
                t: number | string;
                o?: number;
                h?: number;
                l?: number;
                c?: number;
            }[];
            if (!bars.length) return;
            applyBarFromApi(bars[bars.length - 1]);
        } catch {
            /* optional */
        }
    }, [chartSymbol, applyBarFromApi]);

    useEffect(() => {
        refreshGex();
        const timer = setInterval(refreshGex, refreshInterval);
        return () => clearInterval(timer);
    }, [refreshGex, refreshInterval]);

    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const chart = createChart(el, {
            width: el.clientWidth || 800,
            height: el.clientHeight || 480,
            ...lwChartColours(readChartTheme()),
            timeScale: {
                ...lwChartColours(readChartTheme()).timeScale,
                timeVisible: true,
                secondsVisible: false,
            },
        });

        chartRef.current = chart;
        const series = chart.addSeries(CandlestickSeries, {
            upColor: isDesignSystemOn() ? "#26a69a" : CANDLE_UP,
            downColor: isDesignSystemOn() ? "#ef5350" : CANDLE_DOWN,
            borderUpColor: isDesignSystemOn() ? "#26a69a" : CANDLE_WICK_UP,
            borderDownColor: isDesignSystemOn() ? "#ef5350" : CANDLE_WICK_DOWN,
            wickUpColor: isDesignSystemOn() ? "#26a69a" : CANDLE_WICK_UP,
            wickDownColor: isDesignSystemOn() ? "#ef5350" : CANDLE_WICK_DOWN,
        }) as ISeriesApi<"Candlestick">;
        candleRef.current = series;

        const flowPrim = new FlowOpportunityMarkersPrimitive();
        flowMarkersPrimitiveRef.current = flowPrim;
        series.attachPrimitive(flowPrim);

        const ro = new ResizeObserver(() => {
            if (chartRef.current && containerRef.current) {
                chartRef.current.applyOptions({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight,
                });
            }
        });
        ro.observe(el);
        setChartReady(true);

        return () => {
            ro.disconnect();
            spotLineRef.current = null;
            flowMarkersPrimitiveRef.current = null;
            chart.remove();
            chartRef.current = null;
            candleRef.current = null;
            setChartReady(false);
        };
    }, []);

    /** Flow ▲/▼ markers on chart pane (time × strike). */
    useEffect(() => {
        const prim = flowMarkersPrimitiveRef.current;
        if (!prim || !showFlowVp) {
            prim?.setMarkers([]);
            return;
        }
        prim.setMarkers(flowOpportunities);
    }, [flowOpportunities, showFlowVp]);

    useEffect(() => {
        if (!chartReady) return;
        candleBuf.current.clear();
        setHasCandles(false);
        setChainSpot(0);
        setEtfLivePrice(0);
        setDisplaySpot(0);
        gexAnchorRef.current = 0;
        prevChartScaleRef.current = 1;
        initialFitDoneRef.current = false;
        if (spotLineRef.current && candleRef.current) {
            try {
                candleRef.current.removePriceLine(spotLineRef.current);
            } catch {
                /* ignore */
            }
            spotLineRef.current = null;
        }
        loadHistoricalPrice();

        setWsStatus("connecting");

        const onEtfAggregate = (agg: ChartStockLiveTick) => {
            const etfPrice = agg.c ?? agg.p ?? agg.vw ?? agg.a;
            if (!etfPrice || etfPrice <= 0) return;
            if (nativeIndexChart) {
                setLiveFeedMode("scaled_ws");
            } else {
                setLiveFeedMode("stock_ws");
            }
            mergeLiveTick(etfPrice, agg.e ?? Date.now());
            lastTickAtRef.current = Date.now();
            setWsStatus("streaming");
        };

        const onIndexAggregate = (agg: ChartStockLiveTick) => {
            const idxPrice = agg.c ?? agg.p ?? agg.vw ?? agg.a;
            if (!idxPrice || idxPrice <= 0) return;
            setLiveFeedMode("index_ws");
            mergeLiveTick(idxPrice, agg.e ?? Date.now(), { indexNative: true });
            lastTickAtRef.current = Date.now();
            setWsStatus("streaming");
        };

        chartStockWebSocketService.subscribeToStock(wsSymbol, onEtfAggregate);
        chartStockWebSocketService.onConnectionChange((connected) => {
            setWsStatus((prev) => {
                if (!connected) return "disconnected";
                if (prev === "streaming") return "streaming";
                return "connected";
            });
        });

        if (nativeIndexChart && polygonIndexSymbol) {
            polygonIndexWebSocketService.subscribe(
                polygonIndexSymbol,
                onIndexAggregate,
            );
        }

        const pollTimer = setInterval(pollLatestBar, LIVE_POLL_MS);
        pollLatestBar();

        const staleTimer = setInterval(() => {
            const age = Date.now() - lastTickAtRef.current;
            if (age > LIVE_POLL_MS * 2 && lastTickAtRef.current > 0) {
                setWsStatus((s) =>
                    s === "disconnected" ? s : "connected",
                );
            }
        }, 5000);

        return () => {
            clearInterval(pollTimer);
            clearInterval(staleTimer);
            chartStockWebSocketService.unsubscribeFromStock(
                wsSymbol,
                onEtfAggregate,
            );
            if (polygonIndexSymbol) {
                polygonIndexWebSocketService.unsubscribe(
                    polygonIndexSymbol,
                    onIndexAggregate,
                );
            }
        };
    }, [
        chartReady,
        wsSymbol,
        chartSymbol,
        nativeIndexChart,
        polygonIndexSymbol,
        loadHistoricalPrice,
        mergeLiveTick,
        pollLatestBar,
    ]);

    const volumeBand = useMemo(() => {
        const min = Number(minVol);
        const max = Number(maxVol);
        return {
            minVolume: minVol.trim() !== "" && Number.isFinite(min) && min > 0 ? min : undefined,
            maxVolume: maxVol.trim() !== "" && Number.isFinite(max) && max > 0 ? max : undefined,
        };
    }, [minVol, maxVol]);

    /**
     * Levels are derived from the cached chain rather than refetched. Changing a DTE or the volume
     * band is a question about data already in hand, so it should not cost a chain request.
     */
    const gexGroups = useMemo<GexGroup[]>(() => {
        if (!chainRows.length) return [];
        return calculateGexByDte(chainRows, Array.from(selectedDtes), volumeBand);
    }, [chainRows, selectedDtes, volumeBand]);

    /**
     * Traded volume present in the chain, so the band inputs can be sized sensibly. Read from the
     * unfiltered rows: a band tight enough to empty the chart is exactly when you need to know what
     * the real range was.
     */
    const observedVolRange = useMemo(() => {
        let min = Infinity;
        let max = 0;
        for (const r of chainRows) {
            const v = Number(r["Volume"] ?? r.volume ?? r.Vol ?? r.vol) || 0;
            if (v <= 0) continue;
            if (v < min) min = v;
            if (v > max) max = v;
        }
        return max > 0 ? { min: min === Infinity ? 0 : min, max } : null;
    }, [chainRows]);

    /**
     * What the band actually did. A filter that silently empties the chart is indistinguishable
     * from a data outage, so say which one this is.
     */
    const bandSummary = useMemo(() => {
        const active =
            volumeBand.minVolume != null || volumeBand.maxVolume != null;
        if (!active || !chainRows.length) return null;
        const lv = gexGroups[0]?.levels;
        if (!lv || lv.contractsUsed === 0) {
            return {
                empty: true,
                text: "Volume band excludes every contract — widen it to see levels",
            };
        }
        return {
            empty: false,
            text: `${lv.contractsUsed.toLocaleString()} contracts in band · ${lv.contractsFiltered.toLocaleString()} filtered`,
        };
    }, [volumeBand, chainRows, gexGroups]);

    const visibleGroups = useMemo(() => {
        const anchorPx = gexAnchor > 0 ? gexAnchor : 0;
        return gexGroups
            .filter(
                (g) =>
                    selectedDtes.has(g.dte) ||
                    selectedDtes.has(String(g.dte)) ||
                    selectedDtes.has(Number(g.dte)),
            )
            .map((g) => ({
                ...g,
                levels:
                    anchorPx > 0
                        ? refineGexLevelsNearSpot(g.levels, anchorPx)
                        : g.levels,
            }));
    }, [gexGroups, selectedDtes, gexAnchor]);

    /**
     * Volume statistics for the strikes actually in play. The busiest strike is where today's
     * attention is; the quietest says how thin the tail is, which is what the band exists to trim.
     * Read from the near-spot strikes so it describes the levels on screen, not the whole chain.
     */
    const volStats = useMemo(() => {
        const strikes = visibleGroups[0]?.levels?.strikes;
        if (!strikes?.length) return null;
        let min: StrikeAgg | null = null;
        let max: StrikeAgg | null = null;
        let total = 0;
        for (const s of strikes) {
            const v = s.callVol + s.putVol;
            if (v <= 0) continue;
            total += v;
            if (!min || v < min.callVol + min.putVol) min = s;
            if (!max || v > max.callVol + max.putVol) max = s;
        }
        if (!min || !max) return null;
        return {
            min: { strike: min.strike, vol: min.callVol + min.putVol },
            max: { strike: max.strike, vol: max.callVol + max.putVol },
            total,
        };
    }, [visibleGroups]);

    /**
     * Today's exposure against the standing book. Open interest alone cannot tell you whether the
     * gamma it implies is being built or unwound — the sign of volume GEX relative to OI GEX can,
     * and that comparison is the reason this chart is not the GEX Chart.
     */
    const gammaDrift = useMemo(() => {
        if (gexBasis === "oi") return null;
        const lv = visibleGroups[0]?.levels;
        if (!lv?.volume) return null;
        const book = lv.netGex;
        const today = lv.volume.netGex;
        if (!Number.isFinite(book) || !Number.isFinite(today) || book === 0) {
            return null;
        }
        const share = Math.abs(today) / Math.abs(book);
        const reinforces = book > 0 === today > 0;
        const pct = share >= 0.01 ? `${(share * 100).toFixed(0)}%` : "<1%";
        return {
            text: `Flow ${reinforces ? "reinforces" : "opposes"} book · ${pct}`,
            color: reinforces ? colors.chartSuccess : colors.chartError,
            hint: reinforces
                ? `Today's volume adds gamma in the same direction as open interest, at ${pct} of its size — the standing levels are being defended.`
                : `Today's volume adds gamma against open interest, at ${pct} of its size — the standing levels are being worked away.`,
        };
    }, [visibleGroups, gexBasis, colors]);

    useEffect(() => {
        const cs = candleRef.current;
        if (!cs || gexAnchor <= 0) return;

        for (const pl of gexLines.current) {
            try {
                cs.removePriceLine(pl);
            } catch {
                /* ignore */
            }
        }
        gexLines.current = [];

        for (const grp of visibleGroups) {
            const lv = grp.levels;
            if (!lv) continue;
            const color = colorForDte(grp.dte);
            const dteLabel = grp.dte === "all" ? "ALL" : `${grp.dte}DTE`;

            /** Draw one exposure series' four levels. */
            const drawSeries = (
                set: GexLevels | GexLevelSet,
                style: number,
                width: 1 | 2,
                prefix: string,
                drawn: number[],
            ) => {
                for (const spec of GEX_LINE_SPECS) {
                    const price = levelNearSpot(set, spec.key, gexAnchor);
                    if (price == null) continue;
                    if (
                        spec.key === "zeroGamma" &&
                        set.gammaFlip != null &&
                        Math.abs(price - set.gammaFlip) < 0.01
                    ) {
                        continue;
                    }
                    // Two series landing on the same strike would stack identical labels; when the
                    // book and today's tape agree, one line already says so.
                    if (drawn.some((p) => Math.abs(p - price) < 0.01)) continue;
                    drawn.push(price);
                    const ds = isDesignSystemOn();
                    const pl = cs.createPriceLine({
                        price,
                        color: ds ? DS_LEVEL_COLOR[spec.key] || color : color,
                        lineWidth: width,
                        lineStyle: ds ? (style === VOL_LINE_STYLE ? 1 : spec.key === "putWall" ? 2 : 0) : style,
                        axisLabelVisible: true,
                        title: `${dteLabel} ${prefix}${spec.short} ${price.toFixed(0)}`,
                    });
                    gexLines.current.push(pl);
                }
            };

            const drawn: number[] = [];
            if (gexBasis === "oi" || gexBasis === "both") {
                drawSeries(lv, OI_LINE_STYLE, 2, "", drawn);
            }
            if ((gexBasis === "volume" || gexBasis === "both") && lv.volume) {
                // Volume levels are the subordinate read when both are shown, so they go on thinner
                // and dotted rather than competing with the standing book.
                drawSeries(
                    lv.volume,
                    VOL_LINE_STYLE,
                    gexBasis === "volume" ? 2 : 1,
                    "V·",
                    drawn,
                );
            }
        }
    }, [visibleGroups, gexAnchor, gexBasis]);

    useEffect(() => {
        const cs = candleRef.current;
        if (!cs || !showFlowVp || gexAnchor <= 0) {
            for (const pl of flowLines.current) {
                try {
                    cs?.removePriceLine(pl);
                } catch {
                    /* ignore */
                }
            }
            flowLines.current = [];
            return;
        }

        for (const pl of flowLines.current) {
            try {
                cs.removePriceLine(pl);
            } catch {
                /* ignore */
            }
        }
        flowLines.current = [];

        const poc = flowProfileLevels.find((l) => l.isPoc);
        if (
            poc &&
            Math.abs(poc.chartPrice - gexAnchor) / gexAnchor <= NEAR_SPOT_PCT
        ) {
            const pl = cs.createPriceLine({
                price: poc.chartPrice,
                color: isDesignSystemOn() ? "#ffb74d" : "#facc15",
                lineWidth: 2,
                lineStyle: 2,
                axisLabelVisible: true,
                title: `Flow POC ${poc.chartPrice.toFixed(0)}`,
            });
            flowLines.current.push(pl);
        }

        for (const lv of flowProfileLevels.filter((l) => l.isKeyLevel && !l.isPoc)) {
            if (Math.abs(lv.chartPrice - gexAnchor) / gexAnchor > NEAR_SPOT_PCT) continue;
            // Design System: flow key levels are levels, never green/red (rule 5) — neutral line, ▲/▼ in the title.
            const dirColor = isDesignSystemOn()
                ? "#b2b5be"
                : lv.direction === "bullish"
                    ? colors.chartSuccess
                    : lv.direction === "bearish"
                      ? colors.chartError
                      : colors.textMuted;
            const pl = cs.createPriceLine({
                price: lv.chartPrice,
                color: dirColor,
                lineWidth: 1,
                lineStyle: 3,
                axisLabelVisible: true,
                title: `${lv.direction === "bullish" ? "▲" : lv.direction === "bearish" ? "▼" : "·"} ${lv.strike.toFixed(0)}`,
            });
            flowLines.current.push(pl);
        }
    }, [flowProfileLevels, showFlowVp, gexAnchor, colors]);

    /** Live spot line — WS/poll updates via applyOptions (no chart reset). */
    useEffect(() => {
        const cs = candleRef.current;
        if (!cs) return;
        if (displaySpot <= 0) {
            if (spotLineRef.current) {
                try {
                    cs.removePriceLine(spotLineRef.current);
                } catch {
                    /* ignore */
                }
                spotLineRef.current = null;
            }
            return;
        }
        const title = `LIVE $${displaySpot.toFixed(2)}`;
        if (spotLineRef.current) {
            spotLineRef.current.applyOptions({
                price: displaySpot,
                title,
                lineWidth: SPOT_LINE_WIDTH,
                lineStyle: isDesignSystemOn() ? 2 : SPOT_LINE_STYLE,
            });
            return;
        }
        spotLineRef.current = cs.createPriceLine({
            price: displaySpot,
            color: spotLineColor(),
            lineWidth: SPOT_LINE_WIDTH,
            lineStyle: isDesignSystemOn() ? 2 : SPOT_LINE_STYLE,
            axisLabelVisible: false,
            title,
        });
    }, [displaySpot, chartReady]);

    /** Re-stack spot line above GEX / flow when those overlays refresh. */
    useEffect(() => {
        const cs = candleRef.current;
        const p = displaySpotRef.current;
        if (!cs || p <= 0 || !spotLineRef.current) return;
        const title = `LIVE $${p.toFixed(2)}`;
        try {
            cs.removePriceLine(spotLineRef.current);
        } catch {
            /* ignore */
        }
        spotLineRef.current = cs.createPriceLine({
            price: p,
            color: spotLineColor(),
            lineWidth: SPOT_LINE_WIDTH,
            lineStyle: isDesignSystemOn() ? 2 : SPOT_LINE_STYLE,
            axisLabelVisible: false,
            title,
        });
    }, [visibleGroups, flowProfileLevels, showFlowVp]);

    const allDteOptions: (number | string)[] = useMemo(
        () => ["all", ...availableDtes.slice(0, 10)],
        [availableDtes],
    );

    const chartHeight = simpleLayout
        ? "min(420px, calc(100vh - 240px))"
        : 480;

    const rtGexAnalyticsSnapshot = useMemo(() => {
        if (!chainRows.length) return null;
        const sym = (gexUnderlying || inputSymbol).toUpperCase();
        try {
            return buildMarketAnalyticsSnapshot(sym, chainRows, null);
        } catch {
            return null;
        }
    }, [chainRows, gexUnderlying, inputSymbol]);

    const rootStyle: React.CSSProperties = simpleLayout
        ? {
              flex: 1,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
          }
        : {};

    return (
        <div style={{ ...fx.root, ...rootStyle }}>
            <div
                className="flex flex-wrap items-center justify-between gap-3 mb-3 shrink-0"
                style={{
                    borderBottom: fx.border,
                    paddingBottom: simpleLayout ? 8 : 12,
                    padding: simpleLayout ? "8px 10px 8px" : undefined,
                }}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        style={fx.hSmall}
                        title="Intraday: how today's trading is building or eroding the gamma levels, against the live price. For the standing book by expiry, use the Vol/OI/SG tab in the GEX group."
                    >
                        {simpleLayout ? "GEX + Flow" : "Real-Time GEX"}
                    </span>
                    {!simpleLayout && (
                        <span style={fx.muted} className="text-xs">
                            today's flow vs the standing book
                        </span>
                    )}
                    <input
                        type="text"
                        value={inputSymbol}
                        onChange={(e) =>
                            setInputSymbol(e.target.value.toUpperCase())
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                setChainUnderlying("");
                            }
                        }}
                        onFocus={(e) => e.target.select()}
                        style={{
                            ...fx.inputXs,
                            width: simpleLayout ? 72 : 96,
                        }}
                        aria-label="GEX symbol"
                    />
                    {indexGex && (
                        <span style={fx.muted} className="text-xs">
                            {priceLabel}
                        </span>
                    )}
                    {!indexGex &&
                        priceLabel !== inputSymbol &&
                        priceLabel !== displaySymbol && (
                            <span style={fx.muted} className="text-xs">
                                {priceLabel}
                            </span>
                        )}
                    <span className="flex flex-wrap gap-1">
                        {INDEX_QUICK_PICKS.map((sym) => (
                            <button
                                key={sym}
                                type="button"
                                className="text-xs px-1.5 py-0.5 rounded"
                                style={{
                                    ...fx.inputXs,
                                    cursor: "pointer",
                                    opacity:
                                        inputSymbol === sym ? 1 : 0.65,
                                }}
                                onClick={() => {
                                    setInputSymbol(sym);
                                    setChainUnderlying("");
                                }}
                            >
                                {sym}
                            </button>
                        ))}
                    </span>
                    <span
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                            background:
                                wsStatus === "streaming"
                                    ? colors.chartSuccess + "33"
                                    : wsStatus === "connected"
                                      ? colors.chartWarning + "33"
                                      : colors.chartError + "33",
                            color:
                                wsStatus === "streaming"
                                    ? colors.chartSuccess
                                    : wsStatus === "connected"
                                      ? colors.chartWarning
                                      : colors.chartError,
                        }}
                    >
                        {wsStatus === "streaming"
                            ? liveFeedBadge(liveFeedMode)
                            : wsStatus === "connected"
                              ? liveFeedMode === "delayed_poll"
                                  ? "DELAYED"
                                  : "WS OK"
                              : wsStatus === "connecting"
                                ? "WS…"
                                : "WS OFF"}
                    </span>
                    {loadingGex && (
                        <span style={fx.muted} className="text-xs">
                            Loading GEX…
                        </span>
                    )}
                    {flowLoading && showFlowVp && (
                        <span style={fx.muted} className="text-xs">
                            Flow…
                        </span>
                    )}
                    {rtlLoading && showRtlVp && (
                        <span style={fx.muted} className="text-xs">
                            RTL…
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowRtlVp((v) => !v)}
                        style={{
                            ...fx.inputXs,
                            cursor: "pointer",
                            fontWeight: showRtlVp ? 700 : 400,
                            background: showRtlVp
                                ? colors.primary + "44"
                                : colors.surface,
                            color: showRtlVp
                                ? colors.primary
                                : colors.textMuted,
                        }}
                        title="Legacy RTL strike profile: volume, delta, or premium buying/selling power"
                    >
                        RTL VP
                    </button>
                    {showRtlVp ? (
                        <span className="flex gap-0.5">
                            {(
                                [
                                    ["delta", "Δ"],
                                    ["volume", "Vol"],
                                    ["premium", "$"],
                                ] as const
                            ).map(([m, label]) => (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() => setRtlMetric(m)}
                                    style={{
                                        ...fx.inputXs,
                                        cursor: "pointer",
                                        fontWeight: rtlMetric === m ? 700 : 400,
                                        background:
                                            rtlMetric === m
                                                ? colors.primary + "33"
                                                : colors.surface,
                                        color:
                                            rtlMetric === m
                                                ? colors.primary
                                                : colors.textMuted,
                                        padding: "2px 6px",
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </span>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => setShowFlowVp((v) => !v)}
                        style={{
                            ...fx.inputXs,
                            cursor: "pointer",
                            fontWeight: showFlowVp ? 700 : 400,
                            background: showFlowVp
                                ? colors.chartWarning + "44"
                                : colors.surface,
                            color: showFlowVp
                                ? colors.chartWarning
                                : colors.textMuted,
                        }}
                        title="Flow-buffer volume profile: POC, key strikes, direction, whale prints"
                    >
                        Flow VP
                    </button>
                </div>
                {lastUpdate && (
                    <span style={fx.muted} className="text-xs">
                        GEX {lastUpdate} · {rowCount} rows · ±
                        {(NEAR_SPOT_PCT * 100).toFixed(0)}% band
                    </span>
                )}
            </div>

            {rtGexAnalyticsSnapshot && (
                <RegimeSnapshotProvider snapshot={rtGexAnalyticsSnapshot}>
                    <div className="mb-2 shrink-0 px-1">
                        <GexAnalyticsStrip />
                    </div>
                </RegimeSnapshotProvider>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
                {allDteOptions.map((v) => {
                    const active =
                        selectedDtes.has(v) ||
                        selectedDtes.has(String(v)) ||
                        (typeof v === "number" &&
                            selectedDtes.has(Number(v)));
                    const dteColor = colorForDte(v);
                    return (
                        <button
                            key={String(v)}
                            type="button"
                            onClick={() => toggleDte(v)}
                            style={{
                                ...fx.inputXs,
                                cursor: "pointer",
                                fontWeight: active ? 700 : 400,
                                background: active ? dteColor : colors.surface,
                                color: active ? "#0f172a" : colors.textMuted,
                                borderColor: active
                                    ? dteColor
                                    : colors.surfaceBorder,
                            }}
                        >
                            {v === "all" ? "ALL" : `${v}DTE`}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex items-center gap-1">
                    <span style={fx.muted} className="text-xs">
                        Exposure
                    </span>
                    {(
                        [
                            ["oi", "OI", "Open interest — the book carried into today"],
                            ["volume", "Volume", "Gamma exposure from contracts traded today"],
                            ["both", "Both", "Standing book (dashed) against today's tape (dotted)"],
                        ] as [GexBasis, string, string][]
                    ).map(([id, label, hint]) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setGexBasis(id)}
                            title={hint}
                            style={{
                                ...fx.inputXs,
                                cursor: "pointer",
                                fontWeight: gexBasis === id ? 700 : 400,
                                background:
                                    gexBasis === id
                                        ? colors.chartWarning + "44"
                                        : colors.surface,
                                color:
                                    gexBasis === id
                                        ? colors.chartWarning
                                        : colors.textMuted,
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-1">
                    <span style={fx.muted} className="text-xs">
                        Vol
                    </span>
                    <input
                        type="number"
                        min={0}
                        value={minVol}
                        onChange={(e) => setMinVol(e.target.value)}
                        placeholder="min"
                        title="Ignore contracts trading below this many lots today — a noise floor for thin strikes"
                        style={{ ...fx.inputXs, width: 68 }}
                    />
                    <span style={fx.muted} className="text-xs">
                        –
                    </span>
                    <input
                        type="number"
                        min={0}
                        value={maxVol}
                        onChange={(e) => setMaxVol(e.target.value)}
                        placeholder="max"
                        title="Ignore contracts trading above this many lots — caps a single outlier strike"
                        style={{ ...fx.inputXs, width: 68 }}
                    />
                    {(minVol || maxVol) && (
                        <button
                            type="button"
                            onClick={() => {
                                setMinVol("");
                                setMaxVol("");
                            }}
                            style={{ ...fx.inputXs, cursor: "pointer" }}
                            title="Clear the volume band"
                        >
                            ×
                        </button>
                    )}
                    {observedVolRange && (
                        <span style={fx.muted} className="text-xs">
                            chain {observedVolRange.min.toLocaleString()}–
                            {observedVolRange.max.toLocaleString()}
                        </span>
                    )}
                </div>

                {bandSummary && (
                    <span
                        style={{
                            color: bandSummary.empty
                                ? colors.chartWarning
                                : colors.textSecondary,
                        }}
                        className="text-xs"
                    >
                        {bandSummary.text}
                    </span>
                )}
            </div>

            {visibleGroups.length > 0 && gexAnchor > 0 && (
                <div
                    className="flex flex-wrap gap-3 mb-3 text-sm"
                    style={fx.nested}
                >
                    {visibleGroups.map((grp) => (
                        <div
                            key={String(grp.dte)}
                            className="flex flex-wrap gap-2"
                            style={{
                                borderLeft: `3px solid ${colorForDte(grp.dte)}`,
                                paddingLeft: 8,
                            }}
                        >
                            <span
                                style={{
                                    color: colorForDte(grp.dte),
                                    fontWeight: 600,
                                }}
                            >
                                {grp.dte === "all" ? "ALL" : `${grp.dte}DTE`}
                            </span>
                            {gexBasis !== "volume" &&
                                GEX_LINE_SPECS.map((spec) => {
                                    const val = levelNearSpot(
                                        grp.levels,
                                        spec.key,
                                        gexAnchor,
                                    );
                                    return (
                                        <span
                                            key={spec.key}
                                            style={{ color: colors.textSecondary }}
                                            title="From open interest"
                                        >
                                            {spec.short}{" "}
                                            <strong style={{ color: colors.text }}>
                                                {val != null
                                                    ? `$${val.toFixed(2)}`
                                                    : "—"}
                                            </strong>
                                        </span>
                                    );
                                })}
                            {gexBasis !== "oi" &&
                                grp.levels.volume &&
                                GEX_LINE_SPECS.map((spec) => {
                                    const val = levelNearSpot(
                                        grp.levels.volume,
                                        spec.key,
                                        gexAnchor,
                                    );
                                    return (
                                        <span
                                            key={`v-${spec.key}`}
                                            style={{ color: colors.textMuted }}
                                            title="From today's traded volume"
                                        >
                                            V·{spec.short}{" "}
                                            <strong
                                                style={{
                                                    color: colors.textSecondary,
                                                }}
                                            >
                                                {val != null
                                                    ? `$${val.toFixed(2)}`
                                                    : "—"}
                                            </strong>
                                        </span>
                                    );
                                })}
                        </div>
                    ))}
                    {gammaDrift && (
                        <span
                            style={{ color: gammaDrift.color }}
                            title={gammaDrift.hint}
                        >
                            {gammaDrift.text}
                        </span>
                    )}
                    {volStats && (
                        <>
                            <span
                                style={{ color: colors.textSecondary }}
                                title="Busiest strike near spot by contracts traded today"
                            >
                                Vol max{" "}
                                <strong style={{ color: colors.text }}>
                                    {volStats.max.vol.toLocaleString()}
                                </strong>{" "}
                                @ {volStats.max.strike}
                            </span>
                            <span
                                style={{ color: colors.textMuted }}
                                title="Quietest strike near spot that still traded today"
                            >
                                Vol min{" "}
                                <strong style={{ color: colors.textSecondary }}>
                                    {volStats.min.vol.toLocaleString()}
                                </strong>{" "}
                                @ {volStats.min.strike}
                            </span>
                            <span
                                style={{ color: colors.textMuted }}
                                title="Total contracts traded across the strikes near spot"
                            >
                                Vol total{" "}
                                <strong style={{ color: colors.textSecondary }}>
                                    {volStats.total.toLocaleString()}
                                </strong>
                            </span>
                        </>
                    )}
                    <span style={{ color: colors.text }}>
                        GEX ref <strong>${gexAnchor.toFixed(2)}</strong>
                    </span>
                    {displaySpot > 0 && (
                        <span style={{ color: spotLineColor() }}>
                            LIVE <strong>${displaySpot.toFixed(2)}</strong>
                            {wsStatus === "streaming"
                                ? ` · ${liveFeedBadge(liveFeedMode)}`
                                : ""}
                        </span>
                    )}
                </div>
            )}

            {hasCandles && barsNotice && (
                <p
                    className="text-xs mb-2"
                    style={{ color: colors.chartWarning }}
                    title="The chart is drawing, but these bars are not fresh."
                >
                    ⚠ {barsNotice}
                </p>
            )}

            {fetchError && (
                <p className="text-sm mb-2" style={{ color: colors.chartError }}>
                    {fetchError}
                </p>
            )}

            <div
                style={{
                    width: "100%",
                    flex: simpleLayout ? 1 : undefined,
                    height: simpleLayout ? chartHeight : 480,
                    minHeight: simpleLayout ? 280 : 480,
                    background: "var(--void)",
                    borderRadius: simpleLayout ? 0 : 8,
                    border: simpleLayout
                        ? "none"
                        : `1px solid ${colors.surfaceBorder}`,
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                {!chartReady || (!hasCandles && !loadingGex) ? (
                    <div className="py-16 text-center text-sm">
                        <p style={{ color: colors.textMuted }}>
                            {!chartReady
                                ? "Initializing chart…"
                                : barsNotice
                                  ? "No OHLC bars"
                                  : "Loading OHLC bars…"}
                        </p>
                        {chartReady && barsNotice && (
                            <>
                                <p
                                    className="mt-2 px-6"
                                    style={{ color: colors.chartWarning }}
                                >
                                    {barsNotice}
                                </p>
                                <button
                                    type="button"
                                    className="mt-3"
                                    onClick={() => {
                                        setBarsNotice("");
                                        void loadHistoricalPrice();
                                    }}
                                    style={{ ...fx.inputXs, cursor: "pointer" }}
                                >
                                    Retry
                                </button>
                            </>
                        )}
                    </div>
                ) : null}
                <div
                    ref={containerRef}
                    style={{
                        width: "100%",
                        height: "100%",
                        minHeight: simpleLayout ? 280 : 480,
                        visibility:
                            chartReady && hasCandles ? "visible" : "hidden",
                    }}
                />
                {chartReady && hasCandles && showFlowVp ? (
                    <FlowVolumeProfileOverlay
                        chart={chartRef.current}
                        series={candleRef.current}
                        levels={flowProfileLevels}
                        enabled={showFlowVp}
                    />
                ) : null}
                {chartReady && hasCandles && showRtlVp ? (
                    <RtlLegacyVolumeProfileOverlay
                        chart={chartRef.current}
                        series={candleRef.current}
                        levels={rtlProfileLevels}
                        metric={rtlMetric}
                        enabled={showRtlVp}
                        underlyingLabel={gexUnderlying}
                    />
                ) : null}
            </div>

            <p
                className={`text-xs mt-2 shrink-0 ${simpleLayout ? "px-2 pb-2" : ""}`}
                style={fx.muted}
            >
                {nativeIndexChart
                    ? `${displaySymbol} chart (${chartSymbol}) · GEX chain ${gexUnderlying}`
                    : indexGex
                      ? `Index ${displaySymbol} · chain ${gexUnderlying} · OHLC ${wsSymbol}`
                      : `Chain ${gexUnderlying} · OHLC ${wsSymbol}`}
                {indexProxy && chartScale > 1.01
                    ? ` · scale ×${chartScale.toFixed(2)}`
                    : ""}{" "}
                {nativeIndexChart
                    ? ` · ${liveFeedBadge(liveFeedMode)}`
                    : ""}{" "}
                · WS + 1m poll every {LIVE_POLL_MS / 1000}s · GEX ±
                {(NEAR_SPOT_PCT * 100).toFixed(0)}% of spot
                {showFlowVp
                    ? flowOpportunities.length > 0
                        ? ` · Flow ${flowOpportunities.length} markers (${flowMeta.total} rows${flowMeta.source ? ` · ${flowMeta.source}` : ""})`
                        : ` · Flow: no ${flowSymbol} prints in 24h${flowMeta.source ? ` (${flowMeta.source})` : ""}`
                    : ""}
                {showRtlVp
                    ? rtlProfileLevels.length > 0
                        ? ` · RTL ${rtlMetric} ${rtlProfileLevels.length} strikes (${gexUnderlying})`
                        : rtlError
                          ? ` · RTL: ${rtlError}`
                          : ` · RTL: no ${gexUnderlying} legacy rows`
                    : ""}
            </p>
        </div>
    );
};

export default RealTimeGexChart;
